import { useAuthStore } from '@/store/useAuthStore'
import { deriveSessionStatus, useSessionStatus } from '@/hooks/useSessionStatus'

const TEAM_SCOPED_ROLES = new Set(['team-admin', 'team-member', 'freelancer'])

function normalizeApiRole(role: string | null | undefined): string | null {
  if (!role) return null
  return role.toString().trim().toLowerCase().replace(/_/g, '-')
}

/**
 * Gate unique pour les queries / mutations API team-scoped.
 * true quand sessionStatus === 'authenticated'.
 */
export function getApiReady(): boolean {
  return deriveSessionStatus(useAuthStore.getState()) === 'authenticated'
}

/** Team / freelancer uniquement — super-admin ne doit jamais appeler les routes team-scoped. */
export function getTeamScopeApiReady(): boolean {
  const state = useAuthStore.getState()
  if (deriveSessionStatus(state) !== 'authenticated') return false
  const role = normalizeApiRole(state.globalRole)
  return role !== null && TEAM_SCOPED_ROLES.has(role)
}

export function useApiReady(): boolean {
  return useSessionStatus() === 'authenticated'
}

export function useTeamScopeApiReady(): boolean {
  const sessionStatus = useSessionStatus()
  const globalRole = useAuthStore((state) => state.globalRole)
  if (sessionStatus !== 'authenticated') return false
  const role = normalizeApiRole(globalRole)
  return role !== null && TEAM_SCOPED_ROLES.has(role)
}

/** Raccourci pour `enabled:` dans useQuery — combine apiReady + condition métier. */
export function useApiReadyEnabled(when = true): boolean {
  return useApiReady() && when
}

/** Queries team-scoped : exige activeTeamId en plus de la session validée. */
export function useTeamApiReady(): boolean {
  const activeTeamId = useAuthStore((state) => state.activeTeamId)
  return useTeamScopeApiReady() && Boolean(activeTeamId)
}
